'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

import { buildGpaCourseSources, mergeScenarioCourses, resolveGpaBaseline, type GpaCourseSource } from './planningContext';
import { buildPlanningHref } from '@/app/planning/planningNavigation';
import { schedulesApi, degreePlansApi } from '@/app/services/api';
import { useAuth } from '@/app/Components/AuthProvider';
import { Button, IconButton } from '@/app/Components/Button';
import { Field, Input, Select } from '@/app/Components/Field';
import { InfoTooltip } from '@/app/Components/InfoTooltip';
import { PageLoading } from '@/app/Components/LoadingBubbles';
import {
  OperationalEvidence,
  OperationalHeader,
  OperationalPage,
  OperationalSection,
  OperationalSource,
  OperationalState,
  OperationalStatus,
} from '@/app/Components/OperationalPage';
import { Tabs } from '@/app/Components/Tabs';
import { CourseIntelligencePicker } from '@/app/gpa-calculator/CourseIntelligencePicker';
import {
  ACADEMIC_POLICY_REVIEWED_AT,
  SCHOLARSHIP_OPTIONS,
  evaluateAcademicMilestones,
  type AcademicMilestone,
} from '@/app/lib/academicMilestones';
import type { GpaCourseIntelligence, GpaCourseSearchResponse } from '@/app/lib/gpaCourseIntelligence';
import {
  RUTGERS_GRADE_OPTIONS,
  isRutgersLetterGrade,
  calculateCourseGrade,
  calculateProjectedGpa,
  calculateRequiredGpa,
  calculateRutgersGpa,
  formatRutgersGpa,
  type CourseAssessment,
  type CourseGradeMode,
} from '@/app/lib/rutgersGpa';
import { supabase } from '@/app/lib/supabase';
import {
  loadUserAcademicData,
  saveGpaPlannerWorkspace,
  type CourseGradeEntry,
  type GpaPlannerScenarioCourse,
  type GpaPlannerState,
} from '@/app/services/userAcademicData';
import type { RutgersCourse } from '@/app/types/interfaces';

type PlannerMode = 'target' | 'course';
type AcademicProfile = { school: string; year: string };

const DEFAULT_SCENARIO: GpaPlannerScenarioCourse[] = [];

const FIXTURE_SCENARIO: GpaPlannerScenarioCourse[] = [
  { id: 'planned-01198112', baseId: '01198112', name: 'Data Structures', credits: 4, grade: 'B+', countsTowardMajor: true },
  { id: 'planned-01640152', baseId: '01640152', name: 'Calculus II for the Mathematical and Physical Sciences', credits: 4, grade: 'A', countsTowardMajor: true },
];

const FIXTURE_INTELLIGENCE: Record<string, GpaCourseIntelligence> = {
  '01198112': {
    baseId: '01198112', displayId: '01:198:112', title: 'Data Structures', credits: 4,
    departmentName: 'Computer Science',
    summary: 'CS112 studies data structures and the algorithms that operate on them, including searching, sorting, hashing, and structure-driven algorithm design.',
    difficulty: { score: 4, reason: 'Official and student sources describe a major step up from CS111.', confidence: 'high' },
    sourceCount: 6, courseHref: '/courses/01198112',
  },
  '01640152': {
    baseId: '01640152', displayId: '01:640:152', title: 'Calculus II for the Mathematical and Physical Sciences', credits: 4,
    departmentName: 'Mathematics', summary: null, difficulty: null, sourceCount: 0, courseHref: '/courses/01640152',
  },
};

const DEFAULT_ASSESSMENTS: CourseAssessment[] = [
  { id: 'assessment-1', name: 'Completed work', weightOrPoints: 60, earned: 85 },
  { id: 'assessment-2', name: 'Final exam', weightOrPoints: 40, earned: null },
];

const FIXTURE_GRADES: CourseGradeEntry[] = [
  { code: '01198111', grade: 'A', credits: 4, term: 'Fall 2025', designation: 'STANDARD', countsTowardMajor: true },
  { code: '01198112', grade: 'B+', credits: 4, term: 'Spring 2026', designation: 'STANDARD', countsTowardMajor: true },
  { code: '01640151', grade: 'A', credits: 4, term: 'Fall 2025', designation: 'STANDARD', countsTowardMajor: true },
  { code: '01355101', grade: 'P', credits: 3, term: 'Spring 2026', designation: 'P' },
];

const FIXTURE_COURSES: RutgersCourse[] = FIXTURE_GRADES.map((grade, index) => ({
  baseId: grade.code,
  id: grade.code,
  courseName: ['Introduction to Computer Science', 'Data Structures', 'Calculus I', 'College Writing'][index],
  fullTitle: ['Introduction to Computer Science', 'Data Structures', 'Calculus I', 'College Writing'][index],
  department: grade.code.slice(2, 5),
  class_number: grade.code.slice(5),
  credits: grade.credits ?? 0,
  campus: 'NB', meetingTimes: [], instructors: [], section: 'FIXTURE', coreRequirements: [], subjectNotes: null,
} as RutgersCourse));

function numberOrUndefined(value: string): number | undefined {
  if (value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function courseCredits(course: RutgersCourse | undefined, grade: CourseGradeEntry): number {
  if (grade.credits != null) return Number(grade.credits) || 0;
  return course ? Number(course.credits) || 0 : 0;
}

function milestoneTone(status: AcademicMilestone['status']): 'positive' | 'warning' | 'neutral' {
  if (status === 'meets') return 'positive';
  if (status === 'at-risk') return 'warning';
  return 'neutral';
}

function milestoneLabel(status: AcademicMilestone['status']) {
  if (status === 'meets') return 'Meets modeled criteria';
  if (status === 'at-risk') return 'Needs attention';
  if (status === 'not-applicable') return 'Not applicable';
  return 'More information needed';
}

function comparisonLabel(projected: number | null | undefined, current: number | null | undefined) {
  if (projected == null || current == null || !Number.isFinite(projected) || !Number.isFinite(current)) return 'Starting GPA unavailable';
  const difference = projected - current;
  if (Math.abs(difference) < 0.0005) return 'About the same as current';
  return `${formatRutgersGpa(Math.abs(difference))} ${difference > 0 ? 'above' : 'below'} current`;
}

function GpaInlineMetric({ label, value, detail, tone }: { label: string; value: React.ReactNode; detail?: React.ReactNode; tone?: string }) {
  return <div className="min-w-0 py-2" data-tone={tone}><span className="block text-xs text-content-muted">{label}</span><strong className="mt-1 block text-xl tabular-nums text-content">{value}</strong>{detail ? <span className="mt-1 block text-xs text-content-muted">{detail}</span> : null}</div>;
}

export default function GpaPlanner({ embedded = false }: { embedded?: boolean }) {
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const fixtureQuery = process.env.NODE_ENV !== 'production' && process.env.NEXT_PUBLIC_ENABLE_AUDIT_FIXTURES === '1' ? searchParams.get('auditFixture') : null;
  const fixture = fixtureQuery === 'planning-feedback' ? 'gpa-planner' : fixtureQuery;
  const isAuditFixture = fixture === 'gpa-planner' || fixture === 'gpa-empty';
  const loadedUserRef = useRef<string | null>(null);
  const activeUserIdRef = useRef<string | null>(user?.id ?? null);
  const renderedUserIdRef = useRef<string | null>(user?.id ?? null);
  renderedUserIdRef.current = user?.id ?? null;
  const loadRevisionRef = useRef(0);
  const gradeBaselineRef = useRef<{ ownerId: string; courseGrades: CourseGradeEntry[] } | null>(null);
  const savedWorkspaceSignatureRef = useRef<string | null>(null);
  const bootstrappedCourseRef = useRef<string | null>(null);
  const recordDetailsRef = useRef<HTMLDetailsElement>(null);

  const [courseSources, setCourseSources] = useState<GpaCourseSource[]>(isAuditFixture ? [{ id: 'fixture-schedule', label: 'Schedule: Fall 2026', courseIds: ['01198112', '01640152'] }] : []);
  const [selectedSource, setSelectedSource] = useState(isAuditFixture ? 'fixture-schedule' : '');
  const [sourceMessage, setSourceMessage] = useState('');
  const [importing, setImporting] = useState(false);
  const [mode, setMode] = useState<PlannerMode>('target');
  const [isLoading, setIsLoading] = useState(!isAuditFixture);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savedContextUnavailable, setSavedContextUnavailable] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'conflict' | 'error'>('idle');
  const [profile, setProfile] = useState<AcademicProfile>(isAuditFixture
    ? { school: 'School of Arts and Sciences', year: 'Junior' }
    : { school: '', year: '' });
  const [completedCourses, setCompletedCourses] = useState<RutgersCourse[]>(isAuditFixture ? FIXTURE_COURSES : []);
  const [courseGrades, setCourseGrades] = useState<CourseGradeEntry[]>(fixture === 'gpa-planner' ? FIXTURE_GRADES : []);
  const courseGradesRef = useRef(courseGrades);
  courseGradesRef.current = courseGrades;
  const [scenarioCourses, setScenarioCourses] = useState<GpaPlannerScenarioCourse[]>(fixture === 'gpa-planner' ? FIXTURE_SCENARIO : DEFAULT_SCENARIO);
  const [simulationGrade, setSimulationGrade] = useState<'A' | 'B+' | 'C'>('A');
  const [courseIntelligence, setCourseIntelligence] = useState<Record<string, GpaCourseIntelligence>>(isAuditFixture ? FIXTURE_INTELLIGENCE : {});
  const [currentGpaOverride, setCurrentGpaOverride] = useState<number | undefined>(fixture === 'gpa-planner' ? 3.667 : undefined);
  const [currentCreditsOverride, setCurrentCreditsOverride] = useState<number | undefined>(fixture === 'gpa-planner' ? 60 : undefined);
  const [targetGpa, setTargetGpa] = useState(3.7);
  const [targetCredits, setTargetCredits] = useState(15);
  const [termLabel, setTermLabel] = useState('Fall 2026');
  const [annualDegreeCredits, setAnnualDegreeCredits] = useState<number | undefined>(24);
  const [currentTermDegreeCredits, setCurrentTermDegreeCredits] = useState<number | undefined>(12);
  const [completedCredits, setCompletedCredits] = useState<number | undefined>(isAuditFixture ? 72 : undefined);
  const [attemptedCredits, setAttemptedCredits] = useState<number | undefined>(isAuditFixture ? 75 : undefined);
  const [consecutiveTermsBelowTwo, setConsecutiveTermsBelowTwo] = useState<number | undefined>(0);
  const [scholarshipAward, setScholarshipAward] = useState(isAuditFixture ? 'Scarlet Scholarship' : '');
  const [scholarshipGpa, setScholarshipGpa] = useState<number | undefined>(isAuditFixture ? 3.667 : undefined);
  const [courseMode, setCourseMode] = useState<CourseGradeMode>('weighted');
  const [courseTarget, setCourseTarget] = useState(90);
  const [assessments, setAssessments] = useState<CourseAssessment[]>(DEFAULT_ASSESSMENTS);

  const workspaceSignature = JSON.stringify({ ownerId: user?.id, currentGpaOverride, currentCreditsOverride, profile, termLabel, annualDegreeCredits, currentTermDegreeCredits, completedCredits, attemptedCredits, consecutiveTermsBelowTwo, scholarshipAward, scholarshipGpa, scenarioCourses, courseGrades });
  const workspaceSignatureRef = useRef(workspaceSignature);
  workspaceSignatureRef.current = workspaceSignature;
  useEffect(() => {
    setSaveState((current) => current === 'saving' || current === 'conflict'
      || (current === 'saved' && savedWorkspaceSignatureRef.current === workspaceSignature) ? current : 'idle');
  }, [workspaceSignature]);

  const applyPlannerState = useCallback((state?: GpaPlannerState) => {
    setCurrentGpaOverride(state?.currentGpaOverride);
    setCurrentCreditsOverride(state?.currentGradedCreditsOverride);
    setProfile((current) => ({
      school: state?.schoolOverride || current.school,
      year: state?.academicYearOverride || current.year,
    }));
    setTermLabel(state?.termLabel || 'Fall 2026');
    setAnnualDegreeCredits(state ? state.annualDegreeCredits : 24);
    setCurrentTermDegreeCredits(state ? state.currentTermDegreeCredits : 12);
    setCompletedCredits(state?.cumulativeCompletedCredits);
    setAttemptedCredits(state?.cumulativeAttemptedCredits);
    setConsecutiveTermsBelowTwo(state ? state.consecutiveTermsBelowTwo : 0);
    setScholarshipAward(state?.scholarshipAward || '');
    setScholarshipGpa(state?.scholarshipGpa);
    setScenarioCourses(state?.scenarioCourses ?? DEFAULT_SCENARIO);
  }, []);

  const load = useCallback(async () => {
    const ownerId = user?.id;
    if (!ownerId || isAuditFixture) return;
    const loadRevision = ++loadRevisionRef.current;
    setIsLoading(true);
    setLoadError(null);
    try {
      const [academic, onboarding] = await Promise.all([
        loadUserAcademicData(ownerId, { fresh: true }),
        supabase.functions.invoke('onboarding', { body: { action: 'get' } }),
      ]);
      if (loadRevisionRef.current !== loadRevision || activeUserIdRef.current !== ownerId || renderedUserIdRef.current !== ownerId) return;
      if (!academic) throw new Error('Academic history was unavailable.');
      // Optional profile context must not block a valid saved academic record.
      setCompletedCourses(academic.completedCourses || []);
      setSavedContextUnavailable(false);
      const loadedGrades = (academic.courseGrades || []).map((grade) => ({ ...grade }));
      setCourseGrades(loadedGrades);
      gradeBaselineRef.current = { ownerId, courseGrades: loadedGrades.map((grade) => ({ ...grade })) };
      savedWorkspaceSignatureRef.current = null;
      setProfile({ school: onboarding.data?.school || '', year: onboarding.data?.year || '' });
      applyPlannerState(academic.gpaPlanner);
      const [schedules, plans] = await Promise.allSettled([schedulesApi.get_all_user_schedules(ownerId), degreePlansApi.getAllUserPlans(ownerId)]);
      if (loadRevisionRef.current !== loadRevision || activeUserIdRef.current !== ownerId || renderedUserIdRef.current !== ownerId) return;
      const sources = buildGpaCourseSources(schedules.status === 'fulfilled' ? schedules.value ?? [] : [], plans.status === 'fulfilled' ? plans.value ?? [] : []);
      setCourseSources(sources);
      setSelectedSource(current => sources.some(source => source.id === current) ? current : sources[0]?.id ?? '');
      setSourceMessage('');
      if (schedules.status === 'rejected' || plans.status === 'rejected') setSourceMessage('Some saved schedules or plans could not load. You can still search for courses.');
      setSaveState('idle');
    } catch (error) {
      if (loadRevisionRef.current !== loadRevision || activeUserIdRef.current !== ownerId || renderedUserIdRef.current !== ownerId) return;
      console.error('Failed to load GPA planner:', error);
      setLoadError('We could not load your saved academic record. Retry before relying on personalized results.');
    } finally {
      if (loadRevisionRef.current === loadRevision && activeUserIdRef.current === ownerId && renderedUserIdRef.current === ownerId) setIsLoading(false);
    }
  }, [applyPlannerState, isAuditFixture, user?.id]);

  useEffect(() => {
    if (isAuditFixture) return;
    const ownerId = user?.id ?? null;
    if (activeUserIdRef.current !== ownerId) {
      loadRevisionRef.current += 1;
      activeUserIdRef.current = ownerId;
      loadedUserRef.current = null;
      gradeBaselineRef.current = null;
      savedWorkspaceSignatureRef.current = null;
      setSavedContextUnavailable(false);
      setSaveState('idle');
    }
    if (!authLoading && !user) router.replace(`/login?redirect=${encodeURIComponent('/planning?view=gpa')}`);
    if (user && loadedUserRef.current !== user.id) {
      loadedUserRef.current = user.id;
      void load();
    }
  }, [authLoading, isAuditFixture, load, router, user]);

  useEffect(() => {
    if (isAuditFixture) return;
    const missing = scenarioCourses
      .map((course) => course.baseId)
      .filter((baseId): baseId is string => Boolean(baseId && !courseIntelligence[baseId]));
    if (!missing.length) return;
    const controller = new AbortController();
    void Promise.all(missing.map(async (baseId) => {
      const response = await fetch(`/api/gpa/course-search?q=${encodeURIComponent(baseId)}`, { signal: controller.signal });
      if (!response.ok) return null;
      const payload = await response.json() as GpaCourseSearchResponse;
      return payload.results.find((result) => result.baseId === baseId) ?? null;
    })).then((results) => {
      const found = results.filter((result): result is GpaCourseIntelligence => Boolean(result));
      if (!found.length) return;
      setCourseIntelligence((current) => Object.fromEntries([
        ...Object.entries(current),
        ...found.map((result) => [result.baseId, result] as const),
      ]));
    }).catch((error) => {
      if (!controller.signal.aborted) console.error('Failed to hydrate saved GPA course intelligence:', error);
    });
    return () => controller.abort();
  }, [courseIntelligence, isAuditFixture, scenarioCourses]);

  const recordCourses = useMemo(() => courseGrades.map((grade, recordIndex) => {
    const normalized = grade.code.replace(/[^0-9]/g, '');
    const course = completedCourses.find((item) => item.baseId === normalized || item.id === grade.code);
    return { ...grade, recordIndex, name: course?.courseName || grade.code, credits: courseCredits(course, grade) };
  }), [completedCourses, courseGrades]);

  const recordGpa = useMemo(() => calculateRutgersGpa(recordCourses.map((course) => ({
    credits: course.credits,
    grade: course.grade,
    designation: course.designation,
    includeInGpa: course.includeInGpa,
  }))), [recordCourses]);
  const majorRecordGpa = useMemo(() => calculateRutgersGpa(recordCourses.filter((course) => course.countsTowardMajor).map((course) => ({ credits: course.credits, grade: course.grade, designation: course.designation, includeInGpa: course.includeInGpa }))), [recordCourses]);
  const baseline = resolveGpaBaseline(recordGpa, currentGpaOverride, currentCreditsOverride, recordCourses.some(course => isRutgersLetterGrade(course.grade) && course.credits <= 0));
  const currentGpa = baseline?.gpa ?? Number.NaN;
  const currentGradedCredits = baseline?.credits ?? Number.NaN;
  const termGpa = useMemo(() => calculateRutgersGpa(scenarioCourses), [scenarioCourses]);
  const missingScenarioCredits = scenarioCourses.some((course) => isRutgersLetterGrade(course.grade) && course.credits <= 0);
  const projected = useMemo(() => calculateProjectedGpa({
    currentGpa, currentGradedCredits, plannedCourses: scenarioCourses,
  }), [currentGpa, currentGradedCredits, scenarioCourses]);
  const projectionAvailable = Boolean(baseline && termGpa.gradedCredits);
  const projectionChange = projectionAvailable && projected.rawGpa != null ? projected.rawGpa - currentGpa : 0;
  const projectionTone = missingScenarioCredits ? 'warning' : projectionChange > 0.0005 ? 'positive' : projectionChange < -0.0005 ? 'warning' : 'neutral';
  const baselineUnavailableReason = recordGpa.invalidEntries > 0
    ? 'Transcript entries need review; enter a verified starting GPA and graded credits.'
    : recordGpa.rawGpa == null || recordGpa.gradedCredits <= 0
      ? 'No usable graded-credit baseline was found; enter a starting GPA and graded credits.'
      : 'The transcript record is incomplete; enter a verified starting GPA and graded credits.';
  const projectionDetail = !baseline ? baselineUnavailableReason : missingScenarioCredits ? 'Review course credits' : !termGpa.gradedCredits ? 'Choose expected grades' : comparisonLabel(projected.rawGpa, currentGpa);
  const majorScenarioCourses = useMemo(() => scenarioCourses.filter((course) => course.countsTowardMajor), [scenarioCourses]);
  const majorProjected = useMemo(() => calculateProjectedGpa({ currentGpa: majorRecordGpa.rawGpa ?? 0, currentGradedCredits: majorRecordGpa.gradedCredits, plannedCourses: majorScenarioCourses }), [majorRecordGpa, majorScenarioCourses]);
  const simulation = useMemo(() => {
    const plannedCourses = scenarioCourses.map((course) => ({ ...course, grade: simulationGrade }));
    const majorCourses = plannedCourses.filter((course) => course.countsTowardMajor);
    return {
      overall: baseline ? calculateProjectedGpa({ currentGpa, currentGradedCredits, plannedCourses }) : null,
      major: calculateProjectedGpa({ currentGpa: majorRecordGpa.rawGpa ?? 0, currentGradedCredits: majorRecordGpa.gradedCredits, plannedCourses: majorCourses }),
    };
  }, [baseline, currentGpa, currentGradedCredits, majorRecordGpa, scenarioCourses, simulationGrade]);
  const target = useMemo(() => calculateRequiredGpa({
    currentGpa, currentGradedCredits, targetGpa, plannedGradedCredits: targetCredits,
  }), [currentGpa, currentGradedCredits, targetCredits, targetGpa]);
  const courseGrade = useMemo(() => calculateCourseGrade(courseMode, assessments, courseTarget), [assessments, courseMode, courseTarget]);
  const milestones = useMemo(() => evaluateAcademicMilestones({
    school: profile.school,
    academicYear: profile.year,
    cumulativeGpa: baseline ? projected.rawGpa : null,
    termGpa: termGpa.rawGpa,
    termLetterCredits: termGpa.gradedCredits,
    hasTermF: scenarioCourses.some((course) => course.grade === 'F'),
    consecutiveTermsBelowTwo,
    cumulativeCompletedCredits: completedCredits,
    cumulativeAttemptedCredits: attemptedCredits,
    annualDegreeCredits,
    currentTermDegreeCredits,
    scholarshipAward,
    scholarshipGpa,
  }), [annualDegreeCredits, attemptedCredits, completedCredits, consecutiveTermsBelowTwo, currentTermDegreeCredits, profile, baseline?.gpa, baseline?.credits, projected.rawGpa, scenarioCourses, scholarshipAward, scholarshipGpa, termGpa]);

  const updateScenario = (id: string, patch: Partial<GpaPlannerScenarioCourse>) => {
    setScenarioCourses((current) => current.map((course) => course.id === id ? { ...course, ...patch } : course));
  };
  const addCatalogCourse = useCallback((course: GpaCourseIntelligence) => {
    setCourseIntelligence((current) => ({ ...current, [course.baseId]: course }));
    setScenarioCourses((current) => current.some((item) => item.baseId === course.baseId)
      ? current
      : [...current, {
        id: `planned-${course.baseId}`,
        baseId: course.baseId,
        name: course.title,
        credits: course.credits ?? 0,
        grade: '',
      }]);
  }, []);
  useEffect(() => {
    const course = (searchParams.get('course') || '').replace(/\D/g, '');
    if (course.length !== 8 || bootstrappedCourseRef.current === course || (!isAuditFixture && isLoading)) return;
    bootstrappedCourseRef.current = course;
    const controller = new AbortController();
    void fetch(`/api/gpa/course-search?q=${encodeURIComponent(course)}`, { signal: controller.signal })
      .then(async (response) => response.ok ? response.json() as Promise<GpaCourseSearchResponse> : Promise.reject(new Error()))
      .then((payload) => {
        const exact = payload.results.find((result) => result.baseId === course);
        if (exact) addCatalogCourse(exact);
      })
      .catch((error) => { if (!controller.signal.aborted) console.error('Could not add Explore course to GPA planner:', error); });
    return () => controller.abort();
  }, [addCatalogCourse, isAuditFixture, isLoading, searchParams]);
  const importSource = async () => {
    const source = courseSources.find(item => item.id === selectedSource);
    if (!source) return;
    setImporting(true);
    try {
      const results = await Promise.all(source.courseIds.map(async baseId => {
        if (isAuditFixture) return FIXTURE_INTELLIGENCE[baseId] ?? null;
        const response = await fetch(`/api/gpa/course-search?q=${encodeURIComponent(baseId)}`);
        if (!response.ok) return null;
        const payload = await response.json() as GpaCourseSearchResponse;
        return payload.results.find(course => course.baseId === baseId) ?? null;
      }));
      const courses = results.filter((course): course is GpaCourseIntelligence => Boolean(course));
      setScenarioCourses(current => mergeScenarioCourses(current, courses.map(course => ({ id: `planned-${course.baseId}`, baseId: course.baseId, name: course.title, credits: course.credits ?? 0, grade: '' }))));
      setCourseIntelligence(current => ({ ...current, ...Object.fromEntries(courses.map(course => [course.baseId, course])) }));
      setSourceMessage(`${courses.length} courses matched. Existing grades kept.${courses.length < source.courseIds.length ? ` ${source.courseIds.length - courses.length} could not be matched; search for them above.` : ''}`);
    } catch { setSourceMessage('Courses could not be loaded. Your scenario is unchanged; try again.'); }
    finally { setImporting(false); }
  };
  const updateRecord = (index: number, patch: Partial<CourseGradeEntry>) => {
    setCourseGrades((current) => current.map((grade, position) => position === index ? { ...grade, ...patch } : grade));
  };
  const updateAssessment = (id: string, patch: Partial<CourseAssessment>) => {
    setAssessments((current) => current.map((item) => item.id === id ? { ...item, ...patch } : item));
  };

  const handleSave = async () => {
    if (!user?.id || isAuditFixture) {
      setSaveState('saved');
      return;
    }
    if (saveState === 'saving' || saveState === 'conflict' || loadError) return;
    const ownerId = user.id;
    const gradeBaseline = gradeBaselineRef.current;
    if (!gradeBaseline || gradeBaseline.ownerId !== ownerId) {
      setSaveState('error');
      return;
    }
    setSaveState('saving');
    const saveLoadRevision = loadRevisionRef.current;
    const savingSignature = workspaceSignatureRef.current;
    const savingGradesSignature = JSON.stringify(courseGrades);
    const plannerState: GpaPlannerState = {
      version: 1,
      currentGpaOverride,
      currentGradedCreditsOverride: currentCreditsOverride,
      schoolOverride: profile.school || undefined,
      academicYearOverride: profile.year || undefined,
      termLabel,
      annualDegreeCredits,
      currentTermDegreeCredits,
      cumulativeCompletedCredits: completedCredits,
      cumulativeAttemptedCredits: attemptedCredits,
      consecutiveTermsBelowTwo,
      scholarshipAward: scholarshipAward || undefined,
      scholarshipGpa,
      scenarioCourses,
    };
    try {
      const result = await saveGpaPlannerWorkspace(ownerId, courseGrades, plannerState, gradeBaseline.courseGrades);
      if (loadRevisionRef.current !== saveLoadRevision || activeUserIdRef.current !== ownerId || renderedUserIdRef.current !== ownerId) return;
      const superseded = workspaceSignatureRef.current !== savingSignature;
      if (result.status === 'conflict') {
        setSaveState('conflict');
        return;
      }
      if (result.status === 'error') {
        setSaveState(superseded ? 'idle' : 'error');
        return;
      }
      const savedCourses = Array.isArray(result.completedCourses) ? result.completedCourses : null;
      if (savedCourses === null) {
        // The write committed, but stale course details cannot support GPA math.
        setCompletedCourses([]);
        setSavedContextUnavailable(true);
      }
      if (superseded && JSON.stringify(courseGradesRef.current) !== savingGradesSignature) {
        // A newer grade edit takes priority. Keep the loaded baseline so the
        // next intentional grade save can detect a concurrent change.
        setSaveState('idle');
        return;
      }
      const savedGrades = result.courseGrades.map((grade) => ({ ...grade }));
      gradeBaselineRef.current = { ownerId, courseGrades: savedGrades.map((grade) => ({ ...grade })) };
      setCourseGrades(savedGrades);
      if (savedCourses !== null) {
        setCompletedCourses(savedCourses);
        setSavedContextUnavailable(false);
      }
      if (superseded) {
        // The newer scenario remains unsaved, but these grades are the record
        // that the completed save actually used.
        setSaveState('idle');
        return;
      }
      savedWorkspaceSignatureRef.current = JSON.stringify({
        ...(JSON.parse(savingSignature) as Record<string, unknown>),
        courseGrades: savedGrades,
      });
      setSaveState('saved');
    } catch (error) {
      if (loadRevisionRef.current !== saveLoadRevision || activeUserIdRef.current !== ownerId || renderedUserIdRef.current !== ownerId) return;
      console.error('Failed to save GPA planner:', error);
      setSaveState(workspaceSignatureRef.current === savingSignature ? 'error' : 'idle');
    }
  };

  const saveDisabled = Boolean(loadError) || saveState === 'saving' || saveState === 'conflict'
    || (!isAuditFixture && gradeBaselineRef.current?.ownerId !== user?.id);

  if (!isAuditFixture && (authLoading || isLoading || (user && activeUserIdRef.current !== user.id))) return <PageLoading title="Loading GPA planner" />;
  if (!isAuditFixture && !user) return <PageLoading title="Opening sign in" />;
  if (savedContextUnavailable) return (
    <OperationalPage className="space-y-6">
      {!embedded ? <OperationalHeader title="GPA calculator" /> : null}
      <OperationalState
        kind="success"
        title={saveState === 'saved' ? 'Scenario saved; results need reload' : 'Earlier save completed; results need reload'}
        description="The save completed, but course details could not be loaded. GPA results are unavailable until you reload the academic record. Reloading replaces edits made after the save."
        action={<Button intent="secondary" onClick={() => void load()}>Reload record</Button>}
      />
      {loadError ? <OperationalState kind="error" title="Academic record could not reload" description={loadError} /> : null}
    </OperationalPage>
  );

  return (
    <OperationalPage className="space-y-6" data-audit-ready={isAuditFixture ? fixture === 'gpa-empty' ? 'gpa-planner-empty' : 'gpa-planner-populated' : undefined}>
      {!embedded ? <OperationalHeader
        title="GPA calculator"
        actions={<Button intent="primary" onClick={handleSave} disabled={saveDisabled} loading={saveState === 'saving'} loadingLabel="Saving">{saveState === 'saved' ? 'Saved' : 'Save scenario'}</Button>}
      /> : null}

      {loadError ? <OperationalState kind="error" title="Personalized results are unavailable" description={loadError} action={<Button intent="secondary" onClick={() => void load()}>Retry</Button>} /> : null}
      {saveState === 'conflict' ? <OperationalState kind="error" title="Academic record changed" description="The saved academic record changed while this page was open. Reload it before saving again. Reloading replaces edits on this screen." action={<Button intent="secondary" onClick={() => void load()}>Reload record</Button>} /> : null}
      {saveState === 'error' ? <OperationalState kind="error" title="Planner was not saved" description="Your on-screen scenario is still available. Try saving again." action={<Button intent="secondary" onClick={handleSave}>Try again</Button>} /> : null}
      {recordGpa.invalidEntries > 0 ? (
        <OperationalEvidence tone="warning">
          {recordGpa.invalidEntries} saved grade {recordGpa.invalidEntries === 1 ? 'entry uses' : 'entries use'} a value outside the Rutgers grading scale. It is excluded until you review it in Academic record.
        </OperationalEvidence>
      ) : null}

      {!baseline ? (
        <OperationalState
          kind="empty"
          title={recordGpa.gradedCredits === 0 ? 'Add Rutgers course grades for a cumulative estimate' : 'Review your saved grades for a cumulative estimate'}
          description={recordGpa.gradedCredits === 0
            ? 'Add courses and grades from your transcript in Academic record, or enter your starting GPA below.'
            : 'Your saved grade rows do not yet provide a reliable Rutgers GPA baseline. Review your academic record, or enter your starting GPA below.'}
          action={<Button intent="secondary" onClick={() => router.push(buildPlanningHref('record', searchParams))}>Add courses and grades</Button>}
        />
      ) : null}
      <OperationalSection className="min-w-0 !rounded-none !border-0 !bg-transparent !px-0 !shadow-none" title="Courses" actions={embedded ? <Button intent="primary" onClick={handleSave} disabled={saveDisabled} loading={saveState === 'saving'} loadingLabel="Saving">{saveState === 'saved' ? 'Saved' : 'Save scenario'}</Button> : undefined}>
        {(projectionAvailable || baseline || termGpa.gradedCredits > 0) ? <div aria-label="GPA summary" className="mb-5 flex flex-wrap items-end justify-between gap-5 border-b border-line pb-5" data-tone={projectionTone}>
          <div><p className="text-sm text-content-muted">{projectionAvailable ? 'Projected overall GPA' : baseline ? 'Current overall GPA' : 'Term GPA'}</p><p className="mt-1 text-4xl font-semibold tabular-nums text-content">{projectionAvailable ? projected.displayGpa : baseline ? formatRutgersGpa(currentGpa) : termGpa.displayGpa}</p><p className="mt-1 text-xs text-content-muted">{projectionAvailable ? projectionDetail : `${baseline ? currentGradedCredits : termGpa.gradedCredits} graded credits`}</p></div>
          <div className="flex flex-wrap items-center gap-5 text-sm">{projectionAvailable && baseline ? <div><span className="block text-xs text-content-muted">Current overall GPA</span><strong className="tabular-nums">{formatRutgersGpa(currentGpa)}</strong></div> : null}{(projectionAvailable || baseline) && termGpa.gradedCredits > 0 ? <div><span className="block text-xs text-content-muted">Term GPA</span><strong className="tabular-nums">{termGpa.displayGpa}</strong></div> : null}<InfoTooltip title="Rutgers GPA estimate" text="Grades are weighted by course credits. AP and transfer credits do not add grade points. Repeat rules vary by school; this planning estimate is not your official Rutgers GPA." /></div>
        </div> : null}
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_14rem] lg:items-end">
          <CourseIntelligencePicker onSelect={addCatalogCourse} />
          <Field label="Term">{(props) => <Input {...props} value={termLabel} onChange={(event) => setTermLabel(event.target.value)} />}</Field>
        </div>

        {courseSources.length > 0 ? <details className="mt-3">
          <summary className="min-h-12 cursor-pointer py-3 text-sm font-semibold text-[var(--ss-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]">Add saved courses</summary>
          <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-end">
            <Field label="Use saved courses">{props => <Select {...props} value={selectedSource} onChange={event => setSelectedSource(event.target.value)}>{courseSources.map(source => <option key={source.id} value={source.id}>{source.label}</option>)}</Select>}</Field>
            <Button intent="secondary" onClick={importSource} loading={importing} loadingLabel="Adding courses">Add to scenario</Button>
          </div>
        </details> : null}
        {sourceMessage ? <p role="status" className="mt-2 text-sm text-[var(--ss-text-muted)]">{sourceMessage}</p> : null}
        <details className="group mt-4 border-y border-line">
          <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between gap-3 py-2 text-sm font-semibold text-[var(--ss-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]">
            <span>Starting record: {baseline ? `${formatRutgersGpa(currentGpa)} GPA · ${currentGradedCredits} graded credits` : 'Unavailable — enter GPA and graded credits'}</span>
            <ChevronDownIcon className="h-4 w-4 shrink-0 text-[var(--ss-text-muted)] transition-transform duration-150 group-open:rotate-180 motion-reduce:transition-none" aria-hidden="true" />
          </summary>
          <div className="grid gap-4 border-t border-[var(--ss-border)] py-4 sm:grid-cols-2">
            <Field label="Starting GPA" hint={recordGpa.rawGpa == null ? 'From your Rutgers transcript.' : `Saved record: ${recordGpa.displayGpa}.`}>{(props) => <Input {...props} type="number" min="0" max="4" step="0.001" value={currentGpaOverride ?? ''} onChange={(event) => setCurrentGpaOverride(numberOrUndefined(event.target.value))} />}</Field>
            <Field label="Graded credits" hint="Credits already included in that GPA.">{(props) => <Input {...props} type="number" min="0" step="0.5" value={currentCreditsOverride ?? ''} onChange={(event) => setCurrentCreditsOverride(numberOrUndefined(event.target.value))} />}</Field>
          </div>
        </details>

        <div className="mt-6 divide-y divide-[var(--ss-border)] border-y border-[var(--ss-border)]">
          {scenarioCourses.length === 0 ? (
            <div className="py-8 text-center">
              <p className="font-semibold text-[var(--ss-text)]">No courses in this term yet</p>
              <p className="mt-1 text-sm text-[var(--ss-text-muted)]">Search above to add a course.</p>
            </div>
          ) : scenarioCourses.map((course) => {
            const intelligence = course.baseId ? courseIntelligence[course.baseId] : undefined;
            return (
              <article key={course.id} className="py-4">
                <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_3rem] items-end gap-3 lg:grid-cols-[minmax(0,1fr)_7rem_10rem_3rem] lg:items-start lg:gap-4">
                  <div className="col-span-3 min-w-0 lg:col-span-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold text-[var(--ss-text)]">{course.name}</h3>
                      {intelligence ? <span className="font-mono text-xs text-[var(--ss-text-muted)]">{intelligence.displayId}</span> : null}
                      {intelligence && intelligence.credits == null ? <OperationalStatus tone="warning">Credits need review</OperationalStatus> : null}
                    </div>
                    {intelligence && (intelligence.summary || intelligence.difficulty || intelligence.sourceCount) ? (
                      <details className="mt-2 text-sm text-[var(--ss-text-muted)]">
                        <summary className="min-h-12 cursor-pointer py-3 font-semibold text-[var(--ss-brand)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] md:min-h-0 md:py-0">Course details</summary>
                        {intelligence.summary ? <p className="mt-2 max-w-3xl leading-6">{intelligence.summary}</p> : null}
                        {intelligence.difficulty ? <p className="mt-2 leading-6"><strong>AI difficulty {intelligence.difficulty.score}/5</strong> · {intelligence.difficulty.confidence} confidence. {intelligence.difficulty.reason}</p> : null}
                        <p className="mt-2 text-xs">Source-backed AI synthesis; unofficial.</p>
                        {intelligence.sourceCount ? <a className="mt-2 inline-flex min-h-12 items-center gap-1 font-semibold text-[var(--ss-brand)] underline-offset-4 hover:underline md:min-h-0" href={intelligence.courseHref}>{intelligence.sourceCount} cited sources<ArrowTopRightOnSquareIcon aria-hidden="true" className="h-4 w-4" /></a> : null}
                      </details>
                    ) : null}
                  </div>
                  <Field label="Credits" error={course.credits <= 0 ? 'Enter credits' : undefined}>{(props) => <Input {...props} type="number" min="0.5" step="0.5" value={course.credits} onChange={(event) => updateScenario(course.id, { credits: Number(event.target.value) || 0 })} />}</Field>
                  <Field label="Expected grade">{(props) => <Select {...props} value={course.grade} onChange={(event) => updateScenario(course.id, { grade: event.target.value })}><option value="">Choose grade</option>{RUTGERS_GRADE_OPTIONS.map((grade) => <option key={grade} value={grade}>{grade}</option>)}</Select>}</Field>
                  <IconButton label={`Remove ${course.name}`} intent="secondary" onClick={() => setScenarioCourses((current) => current.filter((item) => item.id !== course.id))}><TrashIcon className="h-4 w-4" /></IconButton>
                </div>
                <label className="mt-3 inline-flex min-h-12 cursor-pointer items-center gap-2 text-sm text-[var(--ss-text-muted)]"><input type="checkbox" checked={Boolean(course.countsTowardMajor)} onChange={(event) => updateScenario(course.id, { countsTowardMajor: event.target.checked })} className="h-4 w-4 accent-[var(--ss-brand)]" />Major course</label>
              </article>
            );
          })}
        </div>

        <details className="mt-4 border-b border-[var(--ss-border)] pb-4">
          <summary className="min-h-12 cursor-pointer py-3 text-sm font-semibold text-[var(--ss-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]">Compare grades and major GPA</summary>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <GpaInlineMetric label="Current major GPA" value={majorRecordGpa.displayGpa} detail={`${majorRecordGpa.gradedCredits} tagged graded credits`} />
            <GpaInlineMetric label="Projected major GPA" value={majorScenarioCourses.length ? majorProjected.displayGpa : 'N/A'} detail={majorScenarioCourses.length ? `${majorProjected.gradedCredits} tagged graded credits` : 'Mark planned major courses'} />
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            {recordCourses.length ? <Button intent="quiet" onClick={() => { if (recordDetailsRef.current) { recordDetailsRef.current.open = true; recordDetailsRef.current.scrollIntoView?.({ block: 'start' }); } }}>Choose courses for major GPA</Button> : <span className="text-sm text-[var(--ss-text-muted)]">Major GPA uses courses you tag.</span>}
            <InfoTooltip title="Major GPA" text="Only saved and planned courses you mark as major courses enter this estimate. Your school determines the official major GPA basis." />
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3"><h3 className="text-sm font-semibold text-[var(--ss-text)]">If every planned grade were…</h3><div role="group" aria-label="Simulated grade" className="flex gap-2">{(['A', 'B+', 'C'] as const).map((grade) => <Button key={grade} intent={simulationGrade === grade ? 'primary' : 'secondary'} onClick={() => setSimulationGrade(grade)} aria-pressed={simulationGrade === grade}>{grade}</Button>)}</div></div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <GpaInlineMetric label="Simulated overall" value={scenarioCourses.length && simulation.overall ? simulation.overall.displayGpa : 'N/A'} detail={scenarioCourses.length ? comparisonLabel(simulation.overall?.rawGpa, baseline?.gpa) : 'Add a planned course'} />
            <GpaInlineMetric label="Simulated major" value={majorScenarioCourses.length ? simulation.major.displayGpa : 'N/A'} detail={majorScenarioCourses.length ? comparisonLabel(simulation.major.rawGpa, majorRecordGpa.rawGpa) : 'Mark planned major courses'} />
          </div>
          <p className="mt-3 text-xs text-[var(--ss-text-muted)]">Comparison does not change your expected grades.</p>
        </details>
      </OperationalSection>

      <details className="group border-t border-line py-3">
        <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between gap-3 font-semibold text-[var(--ss-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]">Target GPA and course-grade tools<ChevronDownIcon className="h-5 w-5 shrink-0 text-[var(--ss-text-muted)] transition-transform duration-150 group-open:rotate-180 motion-reduce:transition-none" aria-hidden="true" /></summary>
        <div className="mt-5 border-t border-[var(--ss-border)] pt-5">
          <Tabs
            label="Additional GPA tool"
            activeId={mode}
            onChange={(id) => setMode(id as PlannerMode)}
            items={[{ id: 'target', label: 'Reach a target' }, { id: 'course', label: 'Course grade' }]}
          />

          {mode === 'target' ? (
            <div role="tabpanel" className="mt-5 space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2 rounded-control bg-[var(--ss-surface-subtle)] p-3 text-sm text-[var(--ss-text-muted)]">Starting from {baseline ? `${formatRutgersGpa(currentGpa)} overall GPA across ${currentGradedCredits} graded credits` : 'an unavailable starting record'}. Edit it in Starting record above.</div>
                <Field label="Target cumulative GPA">{(props) => <Input {...props} type="number" min="0" max="4" step="0.001" value={targetGpa} onChange={(event) => setTargetGpa(Number(event.target.value))} />}</Field>
                <Field label="Upcoming graded credits" hint="Do not include Pass/No Credit credits.">{(props) => <Input {...props} type="number" min="0.5" step="0.5" value={targetCredits} onChange={(event) => setTargetCredits(Number(event.target.value))} />}</Field>
              </div>
              <div className="rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] p-5">
                <p className="text-sm font-semibold text-[var(--ss-text-muted)]">Required GPA over upcoming credits</p>
                <p className="mt-1 text-4xl font-bold tabular-nums text-[var(--ss-text)]">{target.requiredGpa == null ? 'N/A' : formatRutgersGpa(target.requiredGpa)}</p>
                <p className="mt-2 text-sm text-[var(--ss-text-muted)]">
                  {target.status === 'impossible' ? 'This target is mathematically out of reach within the entered credits; add more graded credits or adjust the target.' : target.status === 'already-met' ? 'The current GPA already keeps the cumulative result at or above this target.' : target.status === 'achievable' ? 'This target is mathematically achievable within the entered credits.' : 'Enter a valid GPA and positive upcoming-credit count.'}
                </p>
              </div>
            </div>
          ) : null}

          {mode === 'course' ? (
            <div role="tabpanel" className="mt-5 space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Calculation mode">{(props) => <Select {...props} value={courseMode} onChange={(event) => setCourseMode(event.target.value as CourseGradeMode)}><option value="weighted">Weighted categories</option><option value="points">Points based</option></Select>}</Field>
                <Field label="Target course percentage">{(props) => <Input {...props} type="number" min="0" max="100" step="0.1" value={courseTarget} onChange={(event) => setCourseTarget(Number(event.target.value))} />}</Field>
              </div>
              <div className="space-y-3">
                {assessments.map((assessment) => (
                  <div key={assessment.id} className="grid gap-3 rounded-card border border-[var(--ss-border)] p-3 sm:grid-cols-[minmax(0,1fr)_9rem_9rem_auto] sm:items-end">
                    <Field label="Assessment">{(props) => <Input {...props} value={assessment.name} onChange={(event) => updateAssessment(assessment.id, { name: event.target.value })} />}</Field>
                    <Field label={courseMode === 'weighted' ? 'Weight %' : 'Possible points'}>{(props) => <Input {...props} type="number" min="0" step="0.1" value={assessment.weightOrPoints} onChange={(event) => updateAssessment(assessment.id, { weightOrPoints: Number(event.target.value) })} />}</Field>
                    <Field label={courseMode === 'weighted' ? 'Score %' : 'Points earned'} hint="Leave blank if remaining.">{(props) => <Input {...props} type="number" min="0" step="0.1" value={assessment.earned ?? ''} onChange={(event) => updateAssessment(assessment.id, { earned: numberOrUndefined(event.target.value) ?? null })} />}</Field>
                    <IconButton label={`Remove ${assessment.name}`} intent="secondary" onClick={() => setAssessments((current) => current.filter((item) => item.id !== assessment.id))}><TrashIcon className="h-4 w-4" /></IconButton>
                  </div>
                ))}
              </div>
              <Button intent="secondary" onClick={() => setAssessments((current) => [...current, { id: `assessment-${Date.now()}`, name: `Assessment ${current.length + 1}`, weightOrPoints: courseMode === 'weighted' ? 0 : 100, earned: null }])}><PlusIcon className="h-4 w-4" />Add assessment</Button>
              <div className="grid gap-3 sm:grid-cols-2">
                <GpaInlineMetric label="Current grade on completed work" value={courseGrade.currentPercent == null ? 'N/A' : `${courseGrade.currentPercent.toFixed(1)}%`} detail={`${courseGrade.completedShare} ${courseMode === 'weighted' ? 'weight completed' : 'points possible completed'}`} />
                <GpaInlineMetric label="Average needed on remaining work" value={courseGrade.requiredRemainingPercent == null ? 'N/A' : `${courseGrade.requiredRemainingPercent.toFixed(1)}%`} detail={courseGrade.status === 'impossible' ? 'Target is not mathematically reachable' : courseGrade.status === 'invalid' ? 'Weights must total 100%' : 'Across all remaining work'} tone={courseGrade.status === 'impossible' || courseGrade.status === 'invalid' ? 'warning' : 'positive'} />
              </div>
              <OperationalEvidence>Course percentage-to-letter-grade cutoffs come from the syllabus, not a university-wide percentage table. Use the calculated percentage with the course’s own grading policy.</OperationalEvidence>
            </div>
          ) : null}
        </div>
      </details>

      <details className="group border-t border-line py-3">
        <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between gap-3 font-semibold text-[var(--ss-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]">Scholarships and academic milestones<ChevronDownIcon className="h-5 w-5 shrink-0 text-[var(--ss-text-muted)] transition-transform duration-150 group-open:rotate-180 motion-reduce:transition-none" aria-hidden="true" /></summary>
        <div className="mt-5 border-t border-[var(--ss-border)] pt-5">
          <p className="text-sm text-[var(--ss-text-muted)]">Planning signals only—not official determinations.</p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Rutgers school">{(props) => <Select {...props} value={profile.school} onChange={(event) => setProfile((current) => ({ ...current, school: event.target.value }))}><option value="">Select school</option><option>School of Arts and Sciences</option><option>School of Engineering</option><option>School of Business</option><option>School of Environmental and Biological Sciences</option><option>Other</option></Select>}</Field>
            <Field label="Academic year">{(props) => <Select {...props} value={profile.year} onChange={(event) => setProfile((current) => ({ ...current, year: event.target.value }))}><option value="">Select year</option><option>First-year</option><option>Sophomore</option><option>Junior</option><option>Senior</option></Select>}</Field>
            <Field label="Current-term degree credits">{(props) => <Input {...props} type="number" min="0" step="0.5" value={currentTermDegreeCredits ?? ''} onChange={(event) => setCurrentTermDegreeCredits(numberOrUndefined(event.target.value))} />}</Field>
            <Field label="Academic-year completed credits" hint="Exclude summer for Rutgers merit renewal.">{(props) => <Input {...props} type="number" min="0" step="0.5" value={annualDegreeCredits ?? ''} onChange={(event) => setAnnualDegreeCredits(numberOrUndefined(event.target.value))} />}</Field>
            <Field label="Cumulative completed credits">{(props) => <Input {...props} type="number" min="0" step="0.5" value={completedCredits ?? ''} onChange={(event) => setCompletedCredits(numberOrUndefined(event.target.value))} />}</Field>
            <Field label="Cumulative attempted credits">{(props) => <Input {...props} type="number" min="0" step="0.5" value={attemptedCredits ?? ''} onChange={(event) => setAttemptedCredits(numberOrUndefined(event.target.value))} />}</Field>
            <Field label="Consecutive Fall/Spring terms below 2.000">{(props) => <Input {...props} type="number" min="0" step="1" value={consecutiveTermsBelowTwo ?? ''} onChange={(event) => setConsecutiveTermsBelowTwo(numberOrUndefined(event.target.value))} />}</Field>
            <Field label="Existing Rutgers scholarship" hint="Choose only an award you already hold.">{(props) => <Select {...props} value={scholarshipAward} onChange={(event) => setScholarshipAward(event.target.value)}><option value="">No award selected</option>{SCHOLARSHIP_OPTIONS.map((award) => <option key={award} value={award}>{award}</option>)}</Select>}</Field>
            <Field label="Scholarship review GPA" hint="Use the GPA basis named in your award letter.">{(props) => <Input {...props} type="number" min="0" max="4" step="0.001" value={scholarshipGpa ?? ''} onChange={(event) => setScholarshipGpa(numberOrUndefined(event.target.value))} />}</Field>
          </div>
          <div className="mt-6 divide-y divide-[var(--ss-border)] border-y border-[var(--ss-border)]">
            {milestones.map((milestone) => (
              <details key={milestone.id} className="group py-4">
                <summary className="cursor-pointer list-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0"><h3 className="font-semibold text-[var(--ss-text)]">{milestone.title}</h3><p className="mt-1 text-sm text-[var(--ss-text-muted)]">{milestone.summary}</p></div>
                    <OperationalStatus tone={milestoneTone(milestone.status)} icon={milestone.status === 'meets' ? <CheckCircleIcon className="h-4 w-4" /> : milestone.status === 'at-risk' ? <ExclamationTriangleIcon className="h-4 w-4" /> : undefined}>{milestoneLabel(milestone.status)}</OperationalStatus>
                  </div>
                </summary>
                <div className="mt-3 border-t border-[var(--ss-border)] pt-3 text-sm leading-6 text-[var(--ss-text-muted)]">
                  <p>{milestone.detail}</p>
                  <a className="mt-2 inline-flex min-h-12 items-center gap-1 font-semibold text-[var(--ss-brand)] underline-offset-4 hover:underline md:min-h-0" href={milestone.sourceUrl} target="_blank" rel="noreferrer">{milestone.sourceLabel}<ArrowTopRightOnSquareIcon className="h-4 w-4" /></a>
                </div>
              </details>
            ))}
          </div>
          <OperationalSource source="Official Rutgers policy pages" retrievedAt={ACADEMIC_POLICY_REVIEWED_AT} kind="checked-in" />
        </div>
      </details>

      <details ref={recordDetailsRef} className="group border-t border-line py-3">
        <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between gap-3 font-semibold text-[var(--ss-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]">Academic record and transcript designations<ChevronDownIcon className="h-5 w-5 shrink-0 text-[var(--ss-text-muted)] transition-transform duration-150 group-open:rotate-180 motion-reduce:transition-none" aria-hidden="true" /></summary>
        <div className="mt-5 border-t border-[var(--ss-border)] pt-5">
          {recordCourses.length === 0 ? (
            <OperationalState kind="empty" title="No saved grades" description="Add completed courses and grades from Profile or use the starting GPA fields above." />
          ) : (
            <div className="space-y-3">
            <div className="hidden grid-cols-[minmax(0,1.2fr)_6rem_8rem_9rem_minmax(13rem,1fr)] gap-3 border-b border-[var(--ss-border)] pb-2 text-sm font-semibold text-[var(--ss-text-muted)] lg:grid">
              <span>Course</span><span>Credits</span><span>Grade</span><span>Term</span><span>Transcript designation</span>
            </div>
            {recordCourses.map((course) => {
              const validGrade = RUTGERS_GRADE_OPTIONS.includes(course.grade as (typeof RUTGERS_GRADE_OPTIONS)[number]) || course.grade === 'AP';
              return <div key={`${course.code}-${course.term ?? ''}-${course.recordIndex}`} className="grid gap-3 rounded-card border border-[var(--ss-border)] p-3 lg:grid-cols-[minmax(0,1.2fr)_6rem_8rem_9rem_minmax(13rem,1fr)] lg:items-center lg:rounded-none lg:border-x-0 lg:border-t-0 lg:p-0 lg:pb-3">
                <div><span className="mb-1 block text-sm font-semibold text-[var(--ss-text-muted)] lg:sr-only">Course</span><span className="font-medium text-[var(--ss-text)]">{course.name}</span><label className="mt-1 flex min-h-10 cursor-pointer items-center gap-2 text-xs text-[var(--ss-text-muted)]"><input type="checkbox" checked={Boolean(course.countsTowardMajor)} onChange={(event) => updateRecord(course.recordIndex, { countsTowardMajor: event.target.checked })} className="h-4 w-4 accent-[var(--ss-brand)]" />Counts toward my major GPA</label></div>
                <Field label="Credits" className="lg:[&>label]:sr-only">{(props) => <Input {...props} type="number" min="0" step="0.5" value={course.credits} onChange={(event) => updateRecord(course.recordIndex, { credits: Number(event.target.value) })} />}</Field>
                <Field label="Grade" className="lg:[&>label]:sr-only">{(props) => <Select {...props} value={course.grade} onChange={(event) => updateRecord(course.recordIndex, { grade: event.target.value })}>{course.grade === 'AP' ? <option value="AP">AP credit — excluded from GPA</option> : !validGrade && course.grade ? <option value={course.grade}>{course.grade} — unsupported</option> : null}<option value="">Choose grade</option>{RUTGERS_GRADE_OPTIONS.map((grade) => <option key={grade} value={grade}>{grade}</option>)}</Select>}</Field>
                <Field label="Term" className="lg:[&>label]:sr-only">{(props) => <Input {...props} value={course.term || ''} placeholder="Fall 2025" onChange={(event) => updateRecord(course.recordIndex, { term: event.target.value })} />}</Field>
                <Field label="Transcript designation" className="lg:[&>label]:sr-only">{(props) => <Select {...props} value={course.designation || 'STANDARD'} onChange={(event) => updateRecord(course.recordIndex, { designation: event.target.value as CourseGradeEntry['designation'] })}><option value="STANDARD">Standard</option><option value="E">E — no degree credit or GPA</option><option value="J">J — degree credit, no GPA</option><option value="K">K — GPA, no degree credit</option><option value="P">P — degree credit, no GPA</option><option value="NC">NC — no credit or GPA</option><option value="W">W — withdrawal</option><option value="IN">IN — incomplete</option><option value="TZ">TZ — temporarily not graded</option></Select>}</Field>
              </div>;
            })}
            </div>
          )}
          <OperationalEvidence className="mt-4">Repeated-course replacement is school-specific and is not applied automatically. Review the official record and school policy before excluding an earlier attempt.</OperationalEvidence>
        </div>
      </details>
    </OperationalPage>
  );
}
